import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { GET_SCHEDULES } from '../graphql/queries';
import { RUN_SCHEDULE, UPDATE_SCHEDULE } from '../graphql/mutations';
import { DataTable } from '../components/DataTable';
import OsIcon from '../components/OsIcon';
import { scheduleBlueprintLabel, scheduleBlueprintIcon } from '../utils/os-icons';

export default function Schedules() {
  const { data, loading, error, refetch } = useQuery(GET_SCHEDULES);
  const [updateSchedule] = useMutation(UPDATE_SCHEDULE);
  const [runSchedule] = useMutation(RUN_SCHEDULE);
  const [editingName, setEditingName] = useState(null);
  const [editSchedule, setEditSchedule] = useState('');

  if (loading) return <div className="loading">Loading schedules...</div>;
  if (error) return <div className="error">Failed to load schedules: {error.message}</div>;

  const schedules = data?.listSchedules || [];

  const handleToggle = async (name, currentEnabled) => {
    try {
      await updateSchedule({ variables: { input: { name, enabled: !currentEnabled } } });
      toast.success(`Schedule ${name} ${currentEnabled ? 'suspended' : 'enabled'}`);
      refetch();
    } catch (err) {
      toast.error(`Failed to update schedule: ${err.message}`);
    }
  };

  const handleUpdateSchedule = async (name) => {
    try {
      await updateSchedule({ variables: { input: { name, schedule: editSchedule } } });
      toast.success(`Schedule ${name} updated`);
      setEditingName(null);
      refetch();
    } catch (err) {
      toast.error(`Failed to update schedule: ${err.message}`);
    }
  };

  // Submits the schedule's own workflowSpec. Previously this called triggerBuild
  // with the schedule's blueprint-name, which only exists on scheduled-build -
  // so Run Now errored on the other three schedules.
  const handleRunNow = async (name) => {
    try {
      const { data } = await runSchedule({ variables: { name } });
      toast.success(data?.runSchedule?.message || `Triggered ${name}`);
      refetch();
    } catch (err) {
      toast.error(`Failed to run schedule: ${err.message}`);
    }
  };

  const columns = [
    { accessorKey: 'name', header: 'Name' },
    {
      accessorKey: 'blueprintName',
      header: 'Blueprint',
      cell: ({ row, getValue }) => {
        const v = getValue();
        if (v) return <OsIcon name={v} showLabel />;
        // Maintenance and matrix schedules have no single blueprint; say which
        // rather than rendering a bare dash for three of four rows.
        const Icon = scheduleBlueprintIcon(row.original.name);
        const label = scheduleBlueprintLabel(row.original.name);
        if (!label) return <span style={{ color: '#636e72' }}>{'\u2014'}</span>;
        return (
          <span className="inline-flex items-center gap-1.5" style={{ color: '#636e72' }} title={label}>
            {Icon && <Icon width={20} height={20} aria-hidden="true" />}
            <span>{label}</span>
          </span>
        );
      },
    },
    {
      accessorKey: 'schedule',
      header: 'Schedule (Cron)',
      cell: ({ row }) => {
        const s = row.original;
        if (editingName === s.name) {
          return (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={editSchedule} onChange={(e) => setEditSchedule(e.target.value)} style={{ width: 150 }} />
              <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => handleUpdateSchedule(s.name)}>Save</button>
              <button className="btn" style={{ padding: '4px 12px', fontSize: 12, background: '#dfe6e9' }} onClick={() => setEditingName(null)}>Cancel</button>
            </div>
          );
        }
        return <code>{s.schedule}</code>;
      },
    },
    {
      accessorKey: 'enabled',
      header: 'Enabled',
      cell: ({ getValue }) => (
        <span className={`badge badge-${getValue() ? 'success' : 'error'}`}>
          {getValue() ? 'Active' : 'Suspended'}
        </span>
      ),
    },
    {
      accessorKey: 'lastRun',
      header: 'Last Run',
      cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleString() : 'Never',
    },
    {
      accessorKey: 'nextRun',
      header: 'Next Run',
      cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleString() : '-',
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const s = row.original;
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }}
              onClick={() => handleToggle(s.name, s.enabled)}>
              {s.enabled ? 'Suspend' : 'Enable'}
            </button>
            <button className="btn" style={{ padding: '4px 12px', fontSize: 12, background: '#dfe6e9' }}
              onClick={() => { setEditingName(s.name); setEditSchedule(s.schedule); }}>
              Edit
            </button>
            <button className="btn" style={{ padding: '4px 12px', fontSize: 12, background: '#00b894', color: '#fff' }}
              onClick={() => handleRunNow(s.name)}>
              Run Now
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <h2 className="page-title">Build Schedules</h2>
      <div className="card">
        {schedules.length === 0 ? (
          <p>No scheduled builds configured.</p>
        ) : (
          <DataTable columns={columns} data={schedules} />
        )}
      </div>
    </div>
  );
}
